import classNames from "classnames/bind";
import ClearCache from "common/layout/ClearCache";
import Footer from "common/layout/Footer";
import Header from "common/layout/Header";
import Meta from "common/layout/Meta";
import useWhoAmIContext from "common/providers/whoAmI/useWhoAmIContext";
import { AB_TASTY_ID, COOKIE_YES_ID } from "lib/constants";
import type { ResolvedBaseModel } from "models/baseModel";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import type { ResolvedMeta } from "resolvers/data/metaResolver";
import styles from "./Layout.module.scss";
import useInnerHeight from "hooks/useInnerHeight/useInnerHeight";
import LiveChat from "common/blocks/LiveChat";
import RotatingBanners from "../RotatingBanners";

const cx = classNames.bind(styles);

interface LayoutProps extends ResolvedBaseModel {
    children: React.ReactNode;
    id?: string;
    meta?: ResolvedMeta;
    preview?: boolean;
    overideShowRotatingBanners?: boolean;
}

const Layout = (props: LayoutProps) => {
    const { isLoggedIn, trackingId } = useWhoAmIContext();
    useInnerHeight();

    const site = "https://www.motorpoint.co.uk";

    const router = useRouter();
    const _pathSliceLength = Math.min.apply(Math, [
        router.asPath.indexOf("?") > 0 ? router.asPath.indexOf("?") : router.asPath.length,
        router.asPath.indexOf("#") > 0 ? router.asPath.indexOf("#") : router.asPath.length,
    ]);

    const pageNumber = router.asPath.match(/\/page\/(?<pageNumber>\d+)/)?.groups?.pageNumber;

    let canonicalUrl = site + router.asPath.substring(0, _pathSliceLength);

    canonicalUrl = canonicalUrl.replace(`/page/${pageNumber}`, `?page=${pageNumber}`);

    const overideShowRotatingBanners = !!props.overideShowRotatingBanners;

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://log.cookieyes.com/" />
                <link rel="dns-prefetch" href="https://log.cookieyes.com/" />
                <link rel="preconnect" href="https://cdn-cookieyes.com/" crossOrigin="" />
                <link rel="dns-prefetch" href="https://cdn-cookieyes.com" />
                <link rel="preconnect" href="https://directory.cookieyes.com/" crossOrigin="" />
                <link rel="dns-prefetch" href="https://directory.cookieyes.com/" />
                <link rel="preconnect" href="https://images.motorpoint.co.uk/" />
                <link rel="dns-prefetch" href="https://images.motorpoint.co.uk/" />
                <link rel="preconnect" href="https://assets.motorpoint.co.uk/" />
                <link rel="dns-prefetch" href="https://assets.motorpoint.co.uk/" />
                <link rel="preconnect" href="https://resources.motorpoint.co.uk/" crossOrigin="" />
                <link rel="dns-prefetch" href="https://resources.motorpoint.co.uk/" />
                <link rel="preconnect" href="https://observer.motorpoint.co.uk/" />
                <link rel="dns-prefetch" href="https://observer.motorpoint.co.uk/" />
                <link rel="preconnect" href="https://ajax.googleapis.com/" />
                <link rel="dns-prefetch" href="https://ajax.googleapis.com/" />
                <link rel="preconnect" href="https://assets-eu-01.kc-usercontent.com/" />
                <link rel="dns-prefetch" href="https://assets-eu-01.kc-usercontent.com/" />
                <link rel="preconnect" href="https://www.googletagmanager.com/" />
                <link rel="dns-prefetch" href="https://www.googletagmanager.com/" />
                <link rel="preconnect" href="https://www.googleoptimize.com/" />
                <link rel="dns-prefetch" href="https://www.googleoptimize.com/" />
                <link rel="preconnect" href="https://connect.facebook.net/" />
                <link rel="dns-prefetch" href="https://connect.facebook.net/" />
                <link rel="preconnect" href="https://t.contentsquare.net/" />
                <link rel="dns-prefetch" href="https://t.contentsquare.net/" />
                <link rel="preconnect" href="https://www.googleadservices.com/" />
                <link rel="dns-prefetch" href="https://www.googleadservices.com/" />
                <link rel="preconnect" href="https://script.infinity-tracking.com/" />
                <link rel="dns-prefetch" href="https://script.infinity-tracking.com/" />
                <link rel="preconnect" href="https://ict.infinity-tracking.net/" />
                <link rel="dns-prefetch" href="https://ict.infinity-tracking.net/" />
                <link rel="preconnect" href="https://region1.analytics.google.com/" />
                <link rel="dns-prefetch" href="https://region1.analytics.google.com/" />
                <link rel="preconnect" href="https://api.aws.motorpoint.co.uk/" />
                <link rel="dns-prefetch" href="https://api.aws.motorpoint.co.uk/" />
                <link rel="preconnect" href="https://cdn.imagin.studio/" />
                <link rel="dns-prefetch" href="https://cdn.imagin.studio/" />
                <link rel="preconnect" href="https://www.gstatic.com/" />
                <link rel="dns-prefetch" href="https://www.gstatic.com/" />
                <link rel="preconnect" href="https://ka-f.fontawesome.com/" crossOrigin="" />
                <link rel="dns-prefetch" href="https://ka-f.fontawesome.com/" />
                <link rel="preconnect" href="https://try.abtasty.com/" />
                <link rel="dns-prefetch" href="https://try.abtasty.com/" />
                <link
                    rel="icon"
                    sizes="any"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/favicon.ico"
                />
                <link
                    rel="icon"
                    type="image/svg+xml"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/icon.svg"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/apple-touch-icon.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/favicon-32x32.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/favicon-16x16.png"
                />
                <link
                    rel="manifest"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/site.webmanifest"
                />
                <link
                    rel="mask-icon"
                    href="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/safari-pinned-tab.svg"
                    color="#004996"
                />
                <link rel="canonical" href={canonicalUrl} />
                <meta name="msapplication-TileColor" content="#004996" />
                <meta
                    name="msapplication-config"
                    content="https://images.motorpoint.co.uk/logos/motorpoint/appicons/2023/browserconfig.xml"
                />
                <meta name="theme-color" content="#004996" />
                <meta
                    name="viewport"
                    content={`width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=0`}
                />
                <link
                    rel="preload"
                    href="https://resources.motorpoint.co.uk/fonts/graphik/Graphik-Regular-Web.woff2"
                    as="font"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://resources.motorpoint.co.uk/fonts/graphik/Graphik-Bold-Web.woff2"
                    as="font"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://resources.motorpoint.co.uk/fonts/graphik/Graphik-Regular.woff2"
                    as="font"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://resources.motorpoint.co.uk/fonts/graphik/Graphik-Medium.woff2"
                    as="font"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://resources.motorpoint.co.uk/fonts/graphik/Graphik-Semibold.woff2"
                    as="font"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://resources.motorpoint.co.uk/fonts/graphik/Graphik-Bold.woff2"
                    as="font"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://directory.cookieyes.com/api/v1/ip"
                    as="fetch"
                    crossOrigin=""
                />
                <link
                    rel="preload"
                    href="https://cdn-cookieyes.com/client_data/038b1153c65c24e262591a73/banner.js"
                    as="script"
                />
                <link
                    rel="preload"
                    href="https://cdn-cookieyes.com/assets/images/close.svg"
                    as="image"
                />
            </Head>
            <Script id="cookieyes-init" type="text/javascript">
                {`
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        dataLayer.push(arguments);
                    }
                    gtag("consent", "default", {
                        ad_storage: "denied",
                        ad_user_data: "denied",
                        ad_personalization: "denied",
                        analytics_storage: "denied",
                        functionality_storage: "denied",
                        personalization_storage: "denied",
                        security_storage: "granted",
                        wait_for_update: 2000,
                    });
                    gtag("set", "ads_data_redaction", true);
                `}
            </Script>
            <Script
                id="cookieyes"
                type="text/javascript"
                src={`https://cdn-cookieyes.com/client_data/${COOKIE_YES_ID}/script.js`}
                strategy="beforeInteractive"
            ></Script>
            <Script
                id="abtasty"
                data-cookieyes="cookieyes-performance"
                type="text/javascript"
                src={`https://try.abtasty.com/${AB_TASTY_ID}.js`}
            ></Script>
            <Script type="text/javascript" src="/scripts/livechat.min.js" />
            {props.meta && <Meta {...props.meta} />}
            <a className={cx("skipToContentButton")} href="#start-of-content">
                Skip to content
            </a>
            <ClearCache />
            <Header
                {...props.baseData}
                loggedIn={isLoggedIn}
                overideShowRotatingBanners={overideShowRotatingBanners}
            />
            <main id="start-of-content" className={cx("page-body")} data-kontent-item-id={props.id}>
                {router.pathname !== "/" && (
                    <RotatingBanners
                        rotatingBanners={props.baseData.rotatingBanners}
                        overideShowRotatingBanners={overideShowRotatingBanners}
                    />
                )}
                {props.children}
            </main>
            <Footer {...props.baseData.footer} trackingId={trackingId} />
            <LiveChat />
        </>
    );
};

export default Layout;
